import { useState, useEffect } from 'react';
import {FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import styles from './AnadirPregunta.module.css';
import {useAuth} from '../../../Auth/AuthContext'; 

const EditarPregunta = ({
    pregunta,
    areasDisponibles,
    isOpen,
    onClose,
    onPreguntaUpdated,
    testId
}) => {
    const [editForm, setEditForm] = useState({
        pr_descripcion: '',
        pr_orden: '',
        pr_requerida: false,
        pr_area_clave: null
    });
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const {getToken} = useAuth(); 
    const token = getToken(); 

    useEffect(() => {
        if (isOpen && pregunta) {
            setEditForm({
                pr_descripcion: pregunta.pr_descripcion || '',
                pr_orden: pregunta.pr_orden || '',
                pr_requerida: pregunta.pr_requerida || false,
                pr_area_clave: pregunta.pr_area_clave || null
            });
            setErrors({});
        }
    }, [isOpen, pregunta]);

    const handleFormChange = (field, value) => {
        setEditForm(prev => ({
            ...prev,
            [field]: value
        }));

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!editForm.pr_descripcion?.trim()) {
            newErrors.pr_descripcion = 'La descripción es requerida';
        }

        if (!editForm.pr_orden || editForm.pr_orden < 1) {
            newErrors.pr_orden = 'El orden debe ser mayor a 0';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const getErrorMessage = (error, status) => {
        const statusMessages = {
            400: 'Datos inválidos enviados al servidor',
            401: 'No tienes autorización para realizar esta acción',
            403: 'Permisos insuficientes para editar esta pregunta',
            404: 'La pregunta que intentas editar no existe',
            409: 'Ya existe una pregunta con ese orden en el test',
            422: 'Los datos proporcionados no son válidos',
            500: 'Error interno del servidor. Inténtalo más tarde',
            503: 'Servicio temporalmente no disponible'
        };

        if (error.message && error.message !== 'Error al actualizar la pregunta') {
            return error.message;
        }

        if (statusMessages[status]) {
            return statusMessages[status];
        }

        return 'No se pudieron guardar los cambios. Por favor, inténtalo de nuevo.';
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setSaving(true);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/test/preguntas/editar-pregunta/${pregunta.pr_clave}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(editForm)
            });

            if (response.ok) {
                Swal.fire({
                    icon: 'success',
                    title: '¡Pregunta actualizada!',
                    text: 'Los cambios se han guardado exitosamente.',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#242b57',
                    customClass: {
                        popup: 'popupbonito'
                    }
                });

                onPreguntaUpdated();
                onClose();
            } else {
                let errorData = {};
                let errorMessage = '';

                try {
                    const contentType = response.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                        errorData = await response.json();
                        errorMessage = errorData.message || errorData.error || '';
                    } else {
                        const textError = await response.text();
                        errorMessage = textError || '';
                    }
                } catch (parseError) {
                    console.error('Error al parsear respuesta de error:', parseError);
                }

                const finalError = {
                    message: errorMessage,
                    status: response.status,
                    data: errorData
                };

                throw finalError;
            }
        } catch (error) {
            console.error('Error al actualizar pregunta:', error);

            let errorTitle = 'Error al guardar';
            let errorText = '';

            if (!error.status && (error.name === 'TypeError' || error.message.includes('fetch'))) {
                errorTitle = 'Error de conexión';
                errorText = 'No se pudo conectar con el servidor. Verifica tu conexión a internet.';
            } else {
                errorText = getErrorMessage(error, error.status);
                switch (error.status) {
                    case 400:
                    case 422:
                        errorTitle = 'Datos inválidos';
                        break;
                    case 401:
                    case 403:
                        errorTitle = 'Sin permisos';
                        break;
                    case 404:
                        errorTitle = 'Pregunta no encontrada';
                        break;
                    case 409:
                        errorTitle = 'Conflicto de datos';
                        break;
                    case 500:
                    case 503:
                        errorTitle = 'Error del servidor';
                        break;
                    default:
                        errorTitle = 'Error al guardar';
                }
            }

            Swal.fire({
                icon: 'error',
                title: errorTitle,
                text: errorText,
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito'
                }
            });
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (saving) return;
        onClose();
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === 'Escape' && isOpen && !saving) {
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, saving]);

    if (!isOpen || !pregunta) {
        return null;
    }

    return (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>
            <div className={styles.modalContainer}>
                {/* Header del Modal */}
                <div className={styles.modalHeader}>
                    <h2>
                        Editar Pregunta #{pregunta.pr_orden}
                    </h2>
                    <button
                        className={styles.modalCloseBtn}
                        onClick={handleClose}
                        disabled={saving}
                        title="Cerrar"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Contenido del Modal */}
                <div className={styles.modalContent}>
                    <form className={styles.preguntaForm} onSubmit={(e) => e.preventDefault()}>
                        {/* Descripción */}
                        <div className={styles.preguntaFormGroupWide}>
                            <label>
                                Descripción de la pregunta:
                            </label>
                            <input
                                value={editForm.pr_descripcion}
                                onChange={(e) => handleFormChange('pr_descripcion', e.target.value)}
                                placeholder="Escribe tu pregunta aquí..."
                                className={styles.inputForm}
                                disabled={saving}
                            />
                            {errors.pr_descripcion && (
                                <span className={styles.errorText}>{errors.pr_descripcion}</span>
                            )}
                        </div>

                        {/* Grid de dos columnas */}
                        <div className={styles.preguntaFormGrid}>
                            <div className={styles.preguntaFormGroup}>
                                <label>
                                    Orden:
                                </label>
                                <input
                                    type="number"
                                    value={editForm.pr_orden}
                                    onChange={(e) => handleFormChange('pr_orden', parseInt(e.target.value) || '')}
                                    min="1"
                                    className={styles.inputForm}
                                    disabled={saving}
                                />
                                {errors.pr_orden && (
                                    <span className={styles.errorText}>{errors.pr_orden}</span>
                                )}
                            </div>

                            {/* Área/Respuesta */}
                            <div className={styles.preguntaFormGroup}>
                                <label>
                                    Área/Respuesta:
                                </label>
                                <select
                                    value={editForm.pr_area_clave || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        handleFormChange('pr_area_clave', value ? parseInt(value, 10) : null);
                                    }}
                                    className={styles.inputForm}
                                    disabled={saving}
                                >
                                    <option value="">Sin asignar</option>
                                    {areasDisponibles.map(area => (
                                        <option key={area.ar_clave} value={area.ar_clave}>
                                            {area.ar_codigo} - {area.ar_nombre}
                                        </option>
                                    ))}
                                </select>
                                <div className={styles.formHelp}>
                                    {areasDisponibles.length === 0 && (
                                        <span>No hay áreas disponibles. Configura los resultados primero.</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Checkbox */}
                        <div className={styles.preguntaFormGroupWide}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={editForm.pr_requerida}
                                    onChange={(e) => handleFormChange('pr_requerida', e.target.checked)}
                                    disabled={saving}
                                />
                                <span className={styles.checkmark}></span>
                                <div className={styles.formHelp}>
                                Las preguntas requeridas deben ser respondidas obligatoriamente.
                                </div>
                            </label>
                        </div>
                    </form>
                </div>

                {/* Footer del Modal */}
                <div className={styles.modalFooter}>
                    <div className={styles.preguntaFormActions}>
                        <button
                            className={styles.btnSave}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? (
                            <>
                                <div className={styles.btnSpinner}></div>
                                Guardando...
                                </>
                            ) : (
                                <>
                                Editar Pregunta
                                </>
                                )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditarPregunta;